import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Slider from '@material-ui/core/Slider';
import style from './style/cutoff.scss';  // eslint-disable-line

/**
 * Horizontal score bar with optional range based on https://mui.com/components/slider/
 *
 * @example
 * <CutOff
 *     classes={{
 *         root: classes.root,
 *         scoreBar: classes.scoreBar,
 *         scoreBarRoot: classes.scoreBarRoot,
 *         scoreBarTrack: classes.scoreBarTrack,
 *         scoreBarThumb: classes.scoreBarThumb,
 *         scoreBarRail: classes.scoreBarRail
 *     }}
 *     scoreMin={ scoreMin }
 *     scoreMax={ scoreMax }
 *     scoreValue={ scoreValue }
 *     fill={ false } (currently unused)
 *     disabled={ disabled } (can the score be adjusted by dragging the thumb)
 *     digits={ digits } (how many decimals to show)
 *     valueLabelDisplay={ ["on","auto","off"] } (show the value above the thumb in a balloon)
 *     onChange={ someFunction() } (eg a update ui method)
 *     onChangeCommitted={ someFunction() } (eg a save method)
 *     cutOff={ 3.2 } when provided it will show the cut off range in the slider (currently only when disabled is true)
 *     marks={ [2.5, 3] } use array when slide bar should return specific values, otherwise use boolean to mark based on step value
 * />
 */
class CutOff extends Component {
    constructor(props) {
        super(props);

        this.uniqueId = `slider-${Math.random().toString(36).substr(7)}`;

        this.setCutOffTrackWidth = this.setCutOffTrackWidth.bind(this);
    }

    getSliderClasses() {
        const { classes, cutOff, disabled, scoreValue } = this.props;

        if (!cutOff || !disabled) {
            return {
                root: `${style.scoreBarRoot} ${classes.scoreBarRoot}`,
                track: `${style.scoreBarTrack} ${classes.scoreBarTrack}`,
                thumb: `${style.scoreBarThumb} ${classes.scoreBarThumb}`,
                rail: `${style.scoreBarRail} ${classes.scoreBarRail}`,
                mark: `${style.scoreBarMark} ${classes.scoreBarMark}`,
                markActive: style.scoreBarMarkActive
            };
        }

        // replace default slider with one that supports showing the cutOff range
        return {
            root: `${style.scoreBarRoot} ${classes.scoreBarRoot}`,
            track: `${style.scoreBarTrack__withCutOff} ${classes.scoreBarTrack}`,
            thumb: `${scoreValue >= cutOff ? style.scoreBarThumb__withCutOff__withinRange : style.scoreBarThumb__withCutOff} ${classes.scoreBarThumb}`,
            rail: `${style.scoreBarRail__withCutOff} ${classes.scoreBarRail}`,
            mark: `${style.scoreBarMark} ${classes.scoreBarMark}`,
            markActive: style.scoreBarMarkActive
        };
    }

    setCutOffTrackWidth(id) {
        const { cutOff, scoreMin, scoreMax, classes } = this.props;

        if (document.querySelector(`#${id}`)) {
            const elem = document.querySelector(`#${id} > .MuiSlider-track`);

            if (elem) {

                // calculate width of track based on the cutOff, but remember to
                const trackWidth = ((cutOff - scoreMin) * (100 / (scoreMax - scoreMin))).toFixed(0);

                // replace the existing track element and include the new styles
                elem.outerHTML = `<span class="${style.scoreBarTrack__withCutOff} ${classes.scoreBarTrack} MuiSlider-track" style="width: ${trackWidth}%!important"/>`;
            }
        }
    }

    componentDidMount() {
        this.setCutOffTrackWidth(this.uniqueId);
    }

    componentDidUpdate() {
        this.setCutOffTrackWidth(this.uniqueId);
    }

    render() {
        const {
            scoreMin,
            scoreMax,
            classes,
            scoreValue,
            disabled,
            digits,
            valueLabelDisplay,
            onChange,
            onChangeCommitted,
            valueLabelFormat,
            cutOff,
            marks,
            nullable
        } = this.props;

        if (!scoreValue && !nullable) {
            return null;
        }

        const sliderClasses = this.getSliderClasses();

        // if the scoreBar is disabled, and a cutOff was provided, show the cutOff range in the score bar track
        if (cutOff && disabled) {
            this.setCutOffTrackWidth(this.uniqueId);
        }

        return (
            <section className={ `${style.root} ${classes.root ? classes.root : ''}` }>
                <div className={ `${style.scoreBar} ${classes.scoreBar ? classes.scoreBar : ''}` }>
                    <Slider
                        id={ this.uniqueId }
                        valueLabelDisplay={ valueLabelDisplay }
                        size="large"
                        aria-label="scoreBar"
                        min={ parseFloat(scoreMin.toFixed(digits)) }
                        max={ parseFloat(scoreMax.toFixed(digits)) }
                        value={ parseFloat(scoreValue.toFixed(digits)) }
                        disabled={ disabled }
                        step={ marks && marks.length ? null : 1 / (10 * digits) }
                        classes={ sliderClasses }
                        marks={ marks && marks.length ? marks.map(mark => {
                            return {
                                value: mark
                            }
                        }) : false }
                        onChange={ onChange }
                        onChangeCommitted={ onChangeCommitted }
                        valueLabelFormat={ valueLabelFormat }
                    />
                </div>
            </section>
        );
    }
}

export default CutOff;

CutOff.propTypes = {
    scoreMin: PropTypes.number,
    scoreMax: PropTypes.number,
    scoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fill: PropTypes.bool,
    nullable: PropTypes.bool,
    disabled: PropTypes.bool,
    digits: PropTypes.number,
    valueLabelDisplay: PropTypes.string,
    classes: PropTypes.shape({
        root: PropTypes.string,
        scoreBar: PropTypes.string,
        scoreBarRoot: PropTypes.string,
        scoreBarTrack: PropTypes.string,
        scoreBarThumb: PropTypes.string,
        scoreBarRail: PropTypes.string,
        scoreBarMark: PropTypes.string
    }),
    onChange: PropTypes.func,
    onChangeCommitted: PropTypes.func,
    valueLabelFormat: PropTypes.func,
    cutOff: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    marks: PropTypes.array
};

CutOff.defaultProps = {
    scoreMin: 1,
    scoreMax: 5,
    fill: false,
    nullable: false,
    disabled: true,
    digits: 2,
    valueLabelDisplay: 'auto',
    classes: PropTypes.shape({
        root: '',
        scoreBar: '',
        scoreBarRoot: '',
        scoreBarTrack: '',
        scoreBarThumb: '',
        scoreBarRail: '',
        scoreBarMark: ''
    })
};
